import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
} from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTablePagination, useAppMeta } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import BaseSelect from "components/Shared/Base/BaseSelect";
import { useLazyGetBranchCODUserSummaryQuery } from "store/services/reportApi";
import { Visibility, Search } from "@mui/icons-material";

const BranchCODUserSummary = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();
    const branchId = searchParams.get("branch");
    const branchName = searchParams.get("branchName") || "Branch";
    const pmProvinceId = searchParams.get("pmProvinceId");
    const pmProvinceName = searchParams.get("pmProvinceName") || "";
    const [sortBy, setSortBy] = useState("name:1");
    const [keyword, setKeyword] = useState("");
    const [debouncedKeyword, setDebouncedKeyword] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedKeyword(keyword), 400);
        return () => clearTimeout(timer);
    }, [keyword]);

    const provinceListPath = `/app/crm/reports/cod-branch-report?mode=provinces&pmLevel=branches&pmProvinceId=${pmProvinceId || ""}&pmProvinceName=${encodeURIComponent(pmProvinceName)}`;

    const breadcrumbs = [
        { title: "Reports", path: "/app/crm/reports/list" },
        { title: "Pending Branch COD Report", path: "/app/crm/reports/cod-branch-report" },
        ...(pmProvinceId
            ? [{ title: pmProvinceName || "Province", path: provinceListPath }]
            : []),
        { title: `${branchName} — By User` },
    ];

    const [fetchSummary, { data: summaryData, isLoading, isFetching, error: summaryError }] =
        useLazyGetBranchCODUserSummaryQuery();

    const fetchData = useCallback(() => {
        if (!branchId) {
            setErrorMessage("No branch selected.");
            return;
        }
        const [field, direction] = sortBy.split(":");
        fetchSummary({
            branchId,
            params: {
                page: currentPage,
                limit: perPage,
                sortedBy: field,
                sortDirection: direction,
                keyword: debouncedKeyword?.trim() || undefined,
            },
        }).catch((err) => {
            setErrorMessage(err?.data?.message || "Failed to fetch user COD summary");
        });
    }, [branchId, fetchSummary, currentPage, perPage, sortBy, debouncedKeyword]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (summaryError) setErrorMessage("Failed to fetch user COD summary.");
    }, [summaryError]);

    const columns = [
        {
            name: "User Name",
            selector: (row) => row.name,
            cell: (row) => row.name || "N/A",
            grow: 1.5,
        },
        {
            name: "Not Paid",
            selector: (row) => row.totalNotPaid,
            cell: (row) => `Rs. ${row.totalNotPaid?.toFixed(2) || "0.00"}`,
        },
        {
            name: "Action",
            cell: (row) => (
                <Tooltip title="View Shipments">
                    <IconButton
                        color="primary"
                        size="small"
                        onClick={() => {
                            const params = new URLSearchParams();
                            params.set("branch", branchId);
                            params.set("branchName", branchName);
                            params.set("userId", row._id);
                            params.set("userName", row.name || "");
                            if (pmProvinceId) {
                                params.set("pmProvinceId", pmProvinceId);
                                params.set("pmProvinceName", pmProvinceName);
                            }
                            navigate(`/app/crm/reports/branch-cod-user-detail?${params.toString()}`);
                        }}
                    >
                        <Visibility />
                    </IconButton>
                </Tooltip>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "100px",
        },
    ];

    useAppMeta(`COD by User — ${branchName}`);

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs}>
            <BreadcrumbLayout.Error error={summaryError} />
            <BreadcrumbLayout.Paper>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        gap: 2,
                        alignItems: "center",
                        px: 2,
                        pt: 2,
                    }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        {branchName} — Pending COD by User
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center",mb:1 }}>
                        <TextField
                            size="small"
                            placeholder="Search user name..."
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            sx={{ minWidth: 220 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <BaseSelect
                            label="Sort By"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            options={[
                                { label: "Name (Asc)", value: "name:1" },
                                { label: "Name (Desc)", value: "name:-1" },
                                { label: "Not Paid (Asc)", value: "totalNotPaid:1" },
                                { label: "Not Paid (Desc)", value: "totalNotPaid:-1" },
                            ]}
                            getOptionLabel={(opt) => opt.label}
                            getOptionValue={(opt) => opt.value}
                            size="small"
                            sx={{ minWidth: 220 }}
                        />
                    </Box>
                </Box>
                {summaryData?.totalNotPaid !== undefined && (
                    <Box sx={{ px: 2, pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Total Not Paid: <strong>Rs. {Number(summaryData.totalNotPaid || 0).toFixed(2)}</strong>
                        </Typography>
                    </Box>
                )}
                {isLoading || isFetching ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : summaryData?.data?.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            No pending COD found for any user at this branch.
                        </Typography>
                    </Box>
                ) : (
                    <AppTable
                        columns={columns}
                        data={summaryData?.data || []}
                        progressPending={isLoading || isFetching}
                        paginationServer
                        paginationPerPage={perPage}
                        paginationTotalRows={summaryData?.totalItems ?? 0}
                        paginationDefaultPage={currentPage}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                    />
                )}
                <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={() => setErrorMessage("")}>
                    <Alert severity="error" onClose={() => setErrorMessage("")}>
                        {errorMessage}
                    </Alert>
                </Snackbar>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default BranchCODUserSummary;
